import {
  AircraftSceneRuntime,
  type AircraftArtifactVersionRef,
  type AircraftReferenceChangeReceipt,
  type AircraftSceneBuildTicket,
  type AircraftSceneCandidate,
  type AircraftSceneChannelKey,
  type AircraftScenePartRef,
  type AircraftSceneTransform,
} from "./aircraftSceneRuntime";

export const AIRCRAFT_ARCHITECTURE_STORAGE_KEY =
  "loom.aircraft-architecture-demo/fxc-10468/v1";

export type DemoSwapPhase = "idle" | "verified" | "rejected" | "undone";
export type DemoMigrationPolicy = "reset" | "reject";
export type ControlGeometryMode =
  | "analysis-hinge"
  | "integrated-deformable"
  | "circular-cove"
  | "custom-cove";

export interface DemoSubtree { readonly identity: string; readonly label: string; }

export interface AircraftArchitectureDemoSnapshot {
  readonly acceptedPin: "R12" | "R13";
  readonly pendingPin: "R13" | null;
  readonly swapPhase: DemoSwapPhase;
  readonly definitionRevision: string;
  readonly physicalRevision: string;
  readonly workspaceRevision: string;
  readonly topologyBuilds: number;
  readonly renderRequests: number;
  readonly pickRebuilds: number;
  readonly portDegrees: number;
  readonly starboardDegrees: number;
  readonly poseApplications: number;
  readonly selectedControl: boolean;
  readonly migrationPolicy: DemoMigrationPolicy;
  readonly migrationError: string | null;
  readonly moduleRootIdentity: string;
  readonly moduleSubtreeIdentity: string;
  readonly unaffectedRootIdentity: string;
  readonly controlGeometryMode: ControlGeometryMode;
  readonly controlOmlBuilds: number;
  readonly wingOmlBuilds: number;
  readonly unrelatedBuilds: number;
  readonly bothSides: boolean;
  readonly customProfile: string;
  readonly profileError: string | null;
  readonly geometrySignature: string;
  readonly sectionReferenceRevision: string;
}

interface StoredDemoState {
  readonly schema: "loom.aircraft-architecture-demo-state/v1";
  readonly acceptedPin: "R12" | "R13";
  readonly referenceHistory: readonly AircraftReferenceChangeReceipt[];
  readonly modulePhysical: ReturnType<AircraftSceneRuntime<DemoSubtree>["persistOccurrence"]>;
  readonly portDegrees: number;
  readonly starboardDegrees: number;
  readonly physicalRevision: string;
  readonly workspaceRevision: string;
  readonly selectedControl: boolean;
  readonly controlGeometryMode: ControlGeometryMode;
  readonly controlOmlBuilds: number;
  readonly bothSides: boolean;
  readonly customProfile: string;
  readonly geometrySignature: string;
  readonly cameraSignature?: string;
}
const revision = (value: string) => `sha256:${value}`;
const IDENTITY_TRANSFORM: AircraftSceneTransform = [
  1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1,
];
const REV = {
  abiR12: revision("41da97f587ede572f7ac0bc636a113012eef7f3bd50c0e442ce0ed2fc7a66779"),
  abiR13: revision("2fa5b5e3243fa645e1e8d074875469b1e5a09396c50f47a3bfdff7d9fc62822e"),
  compatibility: revision("7d2530d38ee7257d9619429e652e94aa3f327215ffe1db656341941098001c45"),
  physical: revision("9c511bf7bd03fecab1a08215700726a059a6aaf27ba30472b17d7c1e21c9031e"),
  topologyR12: revision("b1512962af3c43751ca5b9c684bcbee4c452b7c5e529ac737793a28c71e3ebfc"),
  topologyR13: revision("e3c51ae8e82ead754905aa358deab025593b061fabeb91d36f214574ddd8e950"),
  verification: revision("402266fd9c15b5be51edab84a0f1dde8394c2c9f96de8969483e9c110e8625e3"),
  workspace: revision("9803f01968e7eda0c619796359ecae0744b5edf8215b67aa282747a5f09f9e58"),
  workspaceSelected: revision("f7251e18a7b18e6e7d273cba46a20394a046ccd2e3391fc372e3e04f042e5658"),
} as const;

const uuid = (value: number) => `00000000-0000-4000-8000-${value.toString().padStart(12, "0")}`;
const MODULE_LOGICAL_ID = uuid(10468);
const MODULE_ARTIFACT_ID = uuid(10469);
const MODULE_R12: AircraftArtifactVersionRef = Object.freeze({
  logicalId: MODULE_LOGICAL_ID, artifactId: MODULE_ARTIFACT_ID,
  artifactVersionId: uuid(120), contentRevision: REV.abiR12,
});
const MODULE_R13: AircraftArtifactVersionRef = Object.freeze({
  logicalId: MODULE_LOGICAL_ID, artifactId: MODULE_ARTIFACT_ID,
  artifactVersionId: uuid(130), contentRevision: REV.abiR13,
});
const STATIC_VERSION: AircraftArtifactVersionRef = Object.freeze({
  logicalId: uuid(210), artifactId: uuid(211), artifactVersionId: uuid(212),
  contentRevision: REV.topologyR12,
});
const CONTROL_VERSION: AircraftArtifactVersionRef = Object.freeze({
  logicalId: uuid(310), artifactId: uuid(311), artifactVersionId: uuid(312),
  contentRevision: REV.compatibility,
});
const ROTOR: AircraftSceneChannelKey = {
  ownerId: "aircraft/propulsion-port", channelId: "propulsion-port/rotor-speed",
};
const LEGACY_COWL: AircraftSceneChannelKey = {
  ownerId: "aircraft/propulsion-port", channelId: "propulsion-port/legacy-cowl",
};
const COOLING_DOOR: AircraftSceneChannelKey = {
  ownerId: "aircraft/propulsion-port", channelId: "propulsion-port/cooling-door",
};
const CONTROL_PART: AircraftScenePartRef = {
  ownerId: "aircraft/main-wing", partId: "port-aileron", partKind: "feature",
};

function candidate(
  pin: "R12" | "R13",
): AircraftSceneCandidate<DemoSubtree> {
  const isR13 = pin === "R13";
  return {
    compatibilityReceiptRevision: REV.compatibility,
    compatibleStateChannels: isR13 ? [ROTOR, COOLING_DOOR] : [ROTOR, LEGACY_COWL],
    pinnedVersion: isR13 ? MODULE_R13 : MODULE_R12,
    resources: [], selectableParts: [],
    subtree: { identity: `propulsion-module-${pin.toLowerCase()}-subtree`, label: `Propulsion ${pin}` },
    topologyRevision: isR13 ? REV.topologyR13 : REV.topologyR12,
  };
}

function staticCandidate(identity: string, parts: readonly AircraftScenePartRef[] = [])
: AircraftSceneCandidate<DemoSubtree> {
  return {
    compatibilityReceiptRevision: REV.compatibility, compatibleStateChannels: [],
    pinnedVersion: STATIC_VERSION, resources: [], selectableParts: parts,
    subtree: { identity, label: identity }, topologyRevision: REV.topologyR12,
  };
}

function controlCandidate(identity: string): AircraftSceneCandidate<DemoSubtree> {
  return {
    compatibilityReceiptRevision: REV.compatibility, compatibleStateChannels: [],
    pinnedVersion: CONTROL_VERSION, resources: [], selectableParts: [],
    subtree: { identity, label: identity }, topologyRevision: REV.topologyR12,
  };
}

function rotationX(degrees: number): AircraftSceneTransform {
  const angle = degrees * Math.PI / 180;
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  return [1, 0, 0, 0, 0, cosine, -sine, 0, 0, sine, cosine, 0, 0, 0, 0, 1];
}

async function sha256Revision(value: string): Promise<string> {
  const bytes = new Uint8Array(await globalThis.crypto.subtle.digest(
    "SHA-256", new TextEncoder().encode(value),
  ));
  return `sha256:${[...bytes].map((item) => item.toString(16).padStart(2, "0")).join("")}`;
}

function isMode(value: unknown): value is ControlGeometryMode {
  return ["analysis-hinge", "integrated-deformable", "circular-cove", "custom-cove"]
    .includes(value as ControlGeometryMode);
}

function validCustomProfile(value: string): boolean {
  const tokens = value.trim().split(/\s+/);
  if (tokens.length < 3 || tokens.length > 16) return false;
  const points: Array<readonly [number, number]> = [];
  for (const token of tokens) {
    const values = token.split(",");
    if (values.length !== 2) return false;
    const x = Number(values[0]);
    const y = Number(values[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || Math.abs(y) > 0.5) return false;
    points.push([x, y]);
  }
  if (points[0]?.[0] !== 0 || points[points.length - 1]?.[0] !== 1) return false;
  return points.every((point, index) => index === 0 || point[0] > points[index - 1]![0]);
}

export class AircraftArchitectureDemoController {
  private runtime: AircraftSceneRuntime<DemoSubtree>;
  private history: AircraftReferenceChangeReceipt[] = [];
  private acceptedPin: "R12" | "R13" = "R12";
  private pendingPin: "R13" | null = null;
  private phase: DemoSwapPhase = "idle";
  private policy: DemoMigrationPolicy = "reset";
  private migrationError: string | null = null;
  private physicalRevision = REV.physical;
  private workspaceRevision = REV.workspace;
  private selectedControl = false;
  private portDegrees = 0;
  private starboardDegrees = 0;
  private renderRequests = 0;
  private pickRebuilds = 0;
  private operationSequence = 1;
  private controlGeometryMode: ControlGeometryMode = "circular-cove";
  private controlOmlBuilds = 1;
  private readonly wingOmlBuilds = 1;
  private readonly unrelatedBuilds = 0;
  private bothSides = true;
  private customProfile = "0,0 0.35,0.08 0.72,0.05 1,0";
  private profileError: string | null = null;
  private geometrySignature = "circular-cove:neutral-v1";
  private sectionDefinitionRevision: string | null = null;
  private sectionReferenceRevision = REV.compatibility;
  private cameraSignature = "camera:initial";
  private cameraRevisionSequence = 0;
  private lastForwardRunId: string | null = null;
  private pendingTicket: AircraftSceneBuildTicket | null = null;
  private readonly stableBodyRoot: object;
  private readonly stableBodySubtree: object;

  constructor(private readonly storage: Storage | null =
    typeof window === "undefined" ? null : window.localStorage) {
    this.runtime = this.createRuntime();
    const stored = this.readStored();
    this.loadStableOccurrences();
    if (stored) this.restore(stored);
    else this.loadFreshModule();
    this.stableBodyRoot = this.runtime.root("fuselage");
    this.stableBodySubtree = this.runtime.root("fuselage").acceptedSubtree;
    this.runtime.drainInvalidation();
  }

  private createRuntime() {
    return new AircraftSceneRuntime<DemoSubtree>({
      applyPose: () => undefined, disposeSubtree: () => undefined,
      reportCleanupErrors: () => undefined,
      schedulePickRebuild: () => { this.pickRebuilds += 1; },
      scheduleRender: () => { this.renderRequests += 1; },
    });
  }

  private loadStableOccurrences(): void {
    for (const [occurrenceId, subtree] of [
      ["fuselage", "fuselage-root-1A2D"], ["main-wing", "wing-root-7B3A"],
      ["propulsion-starboard", "propulsion-starboard-root-3D90"],
    ] as const) {
      this.runtime.loadOccurrence({
        accepted: staticCandidate(subtree, occurrenceId === "main-wing" ? [CONTROL_PART] : []),
        compatibleState: [], occurrenceId, pinnedVersion: STATIC_VERSION,
        placement: IDENTITY_TRANSFORM, selectedPart: null,
      });
    }
    for (const occurrenceId of ["control-port", "control-starboard"] as const) {
      this.runtime.loadOccurrence({
        accepted: controlCandidate(`${occurrenceId}-root-55E1`), compatibleState: [],
        occurrenceId, pinnedVersion: CONTROL_VERSION,
        placement: IDENTITY_TRANSFORM, selectedPart: null,
      });
    }
  }

  private loadFreshModule(): void {
    this.runtime.loadOccurrence({
      accepted: candidate("R12"),
      compatibleState: [{ ...ROTOR, value: 1850 }, { ...LEGACY_COWL, value: 0 }],
      occurrenceId: "propulsion-port", pinnedVersion: MODULE_R12,
      placement: IDENTITY_TRANSFORM, selectedPart: null,
    });
  }

  private restore(stored: StoredDemoState): void {
    this.acceptedPin = stored.acceptedPin;
    this.history = [...stored.referenceHistory];
    this.portDegrees = stored.portDegrees;
    this.starboardDegrees = stored.starboardDegrees;
    this.physicalRevision = stored.physicalRevision;
    this.workspaceRevision = stored.workspaceRevision;
    this.selectedControl = stored.selectedControl;
    this.controlGeometryMode = stored.controlGeometryMode;
    this.controlOmlBuilds = stored.controlOmlBuilds;
    this.bothSides = stored.bothSides;
    this.customProfile = stored.customProfile;
    this.geometrySignature = stored.geometrySignature;
    this.cameraSignature = stored.cameraSignature ?? "camera:initial";
    this.runtime.reloadOccurrence(
      stored.modulePhysical, candidate(stored.acceptedPin), undefined, stored.referenceHistory,
    );
    if (this.selectedControl) this.runtime.selectPart("main-wing", CONTROL_PART);
    this.lastForwardRunId = [...this.history].reverse().find(
      (row) => row.to.artifactVersionId === MODULE_R13.artifactVersionId,
    )?.operationRunId ?? null;
    this.operationSequence = this.history.length + 1;
  }

  snapshot(): AircraftArchitectureDemoSnapshot {
    const module = this.runtime.snapshot("propulsion-port");
    const controls = [this.runtime.snapshot("control-port"), this.runtime.snapshot("control-starboard")];
    const stable = this.runtime.root("fuselage");
    return Object.freeze({
      acceptedPin: this.acceptedPin, pendingPin: this.pendingPin, swapPhase: this.phase,
      definitionRevision: this.sectionDefinitionRevision
        ?? (this.acceptedPin === "R13" ? REV.abiR13 : REV.abiR12),
      physicalRevision: this.physicalRevision, workspaceRevision: this.workspaceRevision,
      topologyBuilds: module.topologyGenerationCount + controls.reduce(
        (sum, item) => sum + item.topologyGenerationCount, 0,
      ),
      renderRequests: this.renderRequests, pickRebuilds: this.pickRebuilds,
      portDegrees: this.portDegrees, starboardDegrees: this.starboardDegrees,
      poseApplications: controls.reduce((sum, item) => sum + item.poseApplicationCount, 0),
      selectedControl: this.selectedControl, migrationPolicy: this.policy,
      migrationError: this.migrationError,
      moduleRootIdentity: "aircraft/propulsion-port:scene-root",
      moduleSubtreeIdentity: this.runtime.root("propulsion-port").acceptedSubtree.identity,
      unaffectedRootIdentity: stable === this.stableBodyRoot
        && stable.acceptedSubtree === this.stableBodySubtree
        ? "aircraft/fuselage:root-1A2D" : "identity-lost",
      controlGeometryMode: this.controlGeometryMode,
      controlOmlBuilds: this.controlOmlBuilds, wingOmlBuilds: this.wingOmlBuilds,
      unrelatedBuilds: this.unrelatedBuilds, bothSides: this.bothSides,
      customProfile: this.customProfile, profileError: this.profileError,
      geometrySignature: this.geometrySignature,
      sectionReferenceRevision: this.sectionReferenceRevision,
    });
  }

  setMigrationPolicy(value: DemoMigrationPolicy): void {
    this.policy = value;
    this.migrationError = null;
  }

  beginSwap(): void {
    this.migrationError = null;
    if (this.acceptedPin !== "R12") return;
    if (this.policy === "reject") {
      this.phase = "rejected";
      this.migrationError = "state-migration-required · reset-required";
      return;
    }
    const runId = uuid(500 + this.operationSequence++);
    const run = {
      from: MODULE_R12, kind: "artifact-version-reference-change" as const,
      occurrenceId: "propulsion-port", operationRunId: runId, to: MODULE_R13,
    };
    const ticket = this.runtime.beginReferenceChange(run);
    const next = candidate("R13");
    this.runtime.stageCandidate(ticket, next);
    this.runtime.verifyCandidate(ticket, {
      compatibilityReceiptRevision: next.compatibilityReceiptRevision,
      compatibleStateChannels: next.compatibleStateChannels,
      occurrenceId: run.occurrenceId, operationRunId: run.operationRunId,
      pinnedVersion: run.to, receiptRevision: REV.verification,
      topologyRevision: next.topologyRevision, verdict: "accepted",
    });
    this.lastForwardRunId = runId;
    this.pendingTicket = ticket;
    this.pendingPin = "R13";
    this.phase = "verified";
    this.runtime.drainInvalidation();
  }

  commitSwap(): void {
    if (this.phase !== "verified" || !this.pendingTicket) return;
    const receipt = this.runtime.commitCandidate(this.pendingTicket);
    this.history.push(receipt);
    this.acceptedPin = "R13";
    this.pendingPin = null;
    this.pendingTicket = null;
    this.phase = "idle";
    this.runtime.drainInvalidation();
    this.persist();
  }

  undoSwap(): void {
    if (this.acceptedPin !== "R13" || !this.lastForwardRunId) return;
    const run = this.runtime.createUndoOperation(
      "propulsion-port", this.lastForwardRunId, uuid(700 + this.operationSequence++),
    );
    const ticket = this.runtime.beginReferenceChange(run);
    const prior = candidate("R12");
    this.runtime.stageCandidate(ticket, prior);
    this.runtime.verifyCandidate(ticket, {
      compatibilityReceiptRevision: prior.compatibilityReceiptRevision,
      compatibleStateChannels: prior.compatibleStateChannels,
      occurrenceId: run.occurrenceId, operationRunId: run.operationRunId,
      pinnedVersion: run.to, receiptRevision: REV.verification,
      topologyRevision: prior.topologyRevision, verdict: "accepted",
    });
    this.history.push(this.runtime.commitCandidate(ticket));
    this.acceptedPin = "R12";
    this.pendingPin = null;
    this.pendingTicket = null;
    this.phase = "undone";
    this.runtime.drainInvalidation();
    this.persist();
  }

  async applyControlPose(side: "port" | "starboard", degrees: number): Promise<void> {
    const bounded = Math.max(-18, Math.min(18, degrees));
    const poseRevision = await sha256Revision(`control-pose:${side}:${bounded.toFixed(3)}`);
    const occurrenceId = `control-${side}`;
    this.runtime.cachePoseTransform(CONTROL_VERSION, poseRevision, rotationX(bounded));
    this.runtime.applyCachedPose(occurrenceId, poseRevision);
    if (side === "port") this.portDegrees = bounded;
    else this.starboardDegrees = bounded;
    if (this.controlGeometryMode === "integrated-deformable") {
      this.controlOmlBuilds += 1;
      this.geometrySignature = `integrated-deformable:posed-v${this.controlOmlBuilds}`;
    }
    this.physicalRevision = await sha256Revision(
      `aircraft-pose:${this.portDegrees.toFixed(3)}:${this.starboardDegrees.toFixed(3)}`,
    );
    this.runtime.drainInvalidation();
    this.persist();
  }

  async recordCameraRevision(signature: string): Promise<boolean> {
    if (!signature || signature.length > 1024) throw new Error("camera revision signature is invalid");
    const sequence = ++this.cameraRevisionSequence;
    const selectedPart = this.selectedControl ? `${CONTROL_PART.ownerId}:${CONTROL_PART.partId}` : "none";
    const workspaceRevision = await sha256Revision(
      `aircraft-workspace:camera:${signature}:selection:${selectedPart}`,
    );
    if (sequence !== this.cameraRevisionSequence) return false;
    this.cameraSignature = signature;
    this.workspaceRevision = workspaceRevision;
    this.persist();
    return true;
  }
  toggleSelection(): void {
    this.cameraRevisionSequence += 1;
    this.selectedControl = !this.selectedControl;
    this.runtime.selectPart("main-wing", this.selectedControl ? CONTROL_PART : null);
    this.workspaceRevision = this.selectedControl ? REV.workspaceSelected : REV.workspace;
    this.runtime.drainInvalidation();
    this.persist();
  }

  setControlGeometryMode(value: ControlGeometryMode): void {
    this.controlGeometryMode = value;
    this.profileError = null;
    this.controlOmlBuilds += 1;
    this.geometrySignature = `${value}:accepted-v${this.controlOmlBuilds}`;
    this.persist();
  }

  toggleBothSides(): void {
    this.bothSides = !this.bothSides;
    this.persist();
  }

  applyCustomProfile(value: string): boolean {
    if (!validCustomProfile(value)) {
      this.profileError = "invalid-custom-profile · accepted geometry retained";
      return false;
    }
    this.customProfile = value.trim();
    this.profileError = null;
    this.controlGeometryMode = "custom-cove";
    this.controlOmlBuilds += 1;
    this.geometrySignature = `custom-cove:accepted-v${this.controlOmlBuilds}`;
    this.persist();
    return true;
  }

  prepareSectionDefinition(definitionRevision: string, referenceRevision: string): void {
    this.sectionDefinitionRevision = definitionRevision;
    this.sectionReferenceRevision = referenceRevision;
  }

  private persist(): void {
    if (!this.storage) return;
    const value: StoredDemoState = {
      schema: "loom.aircraft-architecture-demo-state/v1",
      acceptedPin: this.acceptedPin, referenceHistory: this.history,
      modulePhysical: this.runtime.persistOccurrence("propulsion-port"),
      portDegrees: this.portDegrees, starboardDegrees: this.starboardDegrees,
      physicalRevision: this.physicalRevision, workspaceRevision: this.workspaceRevision,
      selectedControl: this.selectedControl, controlGeometryMode: this.controlGeometryMode,
      controlOmlBuilds: this.controlOmlBuilds, bothSides: this.bothSides,
      customProfile: this.customProfile, geometrySignature: this.geometrySignature,
      cameraSignature: this.cameraSignature,
    };
    this.storage.setItem(AIRCRAFT_ARCHITECTURE_STORAGE_KEY, JSON.stringify(value));
  }

  private readStored(): StoredDemoState | null {
    if (!this.storage) return null;
    const raw = this.storage.getItem(AIRCRAFT_ARCHITECTURE_STORAGE_KEY);
    if (!raw) return null;
    try {
      const value = JSON.parse(raw) as StoredDemoState;
      if (value.schema !== "loom.aircraft-architecture-demo-state/v1"
          || !["R12", "R13"].includes(value.acceptedPin)
          || !isMode(value.controlGeometryMode)) throw new Error("unsupported demo state");
      return value;
    } catch {
      this.storage.removeItem(AIRCRAFT_ARCHITECTURE_STORAGE_KEY);
      return null;
    }
  }
}
